import { Component, EventEmitter, Input, OnInit, Output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';

import { BillFile, OnboardingContext } from '../../models';
import { CommodityType } from '../../../zentility/_shared/_zen-legacy-common/_models/commodity';
import {
  BillImportMatchRequest,
  BillImportMatchResponse,
  BillImportRequest,
  BillImportResponse,
  LegalEntityMatchInput,
  ServiceAddressMatchInput,
  MeterMatchInput,
  BillMatchInput,
  LegalEntityMatchResult,
  ServiceAddressMatchResult,
  MeterMatchResult,
  MatchSuggestion,
  CustomerMatchResult,
  LegalEntityImport,
  ServiceAddressImport,
  MeterImport,
  BillImport
} from '../../models/bill-import.models';
import { BillImportService } from '../../services/bill-import.service';
import { buildAddressGroupingKey } from '../../utils/address-normalization.util';
import { billCommodity } from '../../utils/bill-utils';
import {
  DISTRIBUTION_BUCKET_KEYS,
  SUPPLY_BUCKET_KEYS,
  gasUnitIdFromName,
  lineItemsForImport,
} from '../../services/bill-persistence.service';
import { BillService } from '../../services/bill.service';

interface SelectOption {
  value: number | string | null;
  label: string;
  score?: number;
  isCreateNew?: boolean;
  matchReasons?: string[];
}

interface NewCustomerPreview {
  suggestedName: string;
  billingAddress: string | null;
  legalEntityNames: string[];
  serviceAddressCount: number;
  meterCount: number;
  billCount: number;
}

@Component({
  selector: 'app-import-review',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './import-review.component.html',
  styleUrls: ['./import-review.component.scss']
})
export class ImportReviewComponent implements OnInit {
  @Input() bills: BillFile[] = [];
  @Input() context!: OnboardingContext;

  @Output() importComplete = new EventEmitter<BillImportResponse>();
  @Output() importCancelled = new EventEmitter<void>();

  // State
  isLoading = signal(false);
  isMatching = signal(false);
  isImporting = signal(false);
  error = signal<string | null>(null);

  // Step state
  currentStep = signal<'customer' | 'review'>('customer');
  needsCustomerStep = signal(false);

  // Customer match
  customerMatch = signal<CustomerMatchResult | null>(null);
  selectedCustomerId = signal<number | string | null>(null);
  selectedCustomerLabel = signal<string>('');

  // Match data
  matchRequest = signal<BillImportMatchRequest | null>(null);
  matchResponse = signal<BillImportMatchResponse | null>(null);

  // User selections: groupId -> selected ID (null = create new)
  selections = signal<Map<string, number | string | null>>(new Map());

  // Pre-computed customer options for the template
  customerOptions = computed<SelectOption[]>(() => {
    const match = this.customerMatch();
    if (!match) return [{ value: null, label: 'Create New Customer', isCreateNew: true }];

    const options: SelectOption[] = [];

    if (match.match) {
      options.push({
        value: match.match.id,
        label: match.match.label,
        score: match.match.score,
        matchReasons: match.match.matchReasons
      });
    }

    for (const alt of match.alternatives || []) {
      if (!match.match || alt.id !== match.match.id) {
        options.push({
          value: alt.id,
          label: alt.label,
          score: alt.score,
          matchReasons: alt.matchReasons
        });
      }
    }

    options.push({ value: null, label: 'Create New Customer', isCreateNew: true });

    return options;
  });

  // Selected customer option details (pre-computed for template)
  selectedCustomerOption = computed<SelectOption | null>(() => {
    const id = this.selectedCustomerId();
    return this.customerOptions().find(o => o.value === id) ?? null;
  });

  // Preview of new customer to be created (derived from bill data)
  newCustomerPreview = computed<NewCustomerPreview>(() => {
    const approvedBills = this.bills.filter(b => b.status === 'APPROVED' && b.data);

    // Derive customer name from legal entity names (the company name from the bills)
    const legalEntityNames = [...new Set(
      approvedBills.map(b => (b.data!.legalEntity || '').trim()).filter(Boolean)
    )];
    const suggestedName = legalEntityNames[0] || 'New Customer';

    // Derive billing address from first bill that has one
    const billWithAddress = approvedBills.find(b =>
      b.data!.billingAddressStreet1?.trim()
    );
    let billingAddress: string | null = null;
    if (billWithAddress) {
      const d = billWithAddress.data!;
      const parts = [
        d.billingAddressStreet1,
        d.billingAddressStreet2,
        [d.billingAddressCity, d.billingAddressState].filter(Boolean).join(', '),
        d.billingAddressZip
      ].filter(Boolean);
      billingAddress = parts.join(', ');
    }

    // Count unique service addresses and meters from match request
    const req = this.matchRequest();
    let serviceAddressCount = 0;
    let meterCount = 0;
    let billCount = 0;
    if (req) {
      for (const le of req.legalEntities) {
        serviceAddressCount += le.serviceAddresses.length;
        for (const sa of le.serviceAddresses) {
          meterCount += sa.meters.length;
          for (const m of sa.meters) {
            billCount += m.bills.length;
          }
        }
      }
    }

    return {
      suggestedName,
      billingAddress,
      legalEntityNames,
      serviceAddressCount,
      meterCount,
      billCount
    };
  });

  // Computed stats broken down by hierarchy level
  stats = computed(() => {
    const response = this.matchResponse();
    if (!response) {
      return {
        legalEntities: { total: 0, new: 0, existing: 0 },
        serviceAddresses: { total: 0, new: 0, existing: 0 },
        meters: { total: 0, new: 0, existing: 0 },
        bills: { total: 0, duplicates: 0 }
      };
    }

    const sel = this.selections();
    const le = { total: 0, new: 0, existing: 0 };
    const sa = { total: 0, new: 0, existing: 0 };
    const m = { total: 0, new: 0, existing: 0 };
    const b = { total: 0, duplicates: 0 };

    for (const len of response.legalEntities) {
      le.total++;
      if (sel.get(len.groupId) === null) le.new++;
      else le.existing++;

      for (const addr of len.serviceAddresses) {
        sa.total++;
        if (sel.get(addr.groupId) === null) sa.new++;
        else sa.existing++;

        for (const meter of addr.meters) {
          m.total++;
          if (sel.get(meter.groupId) === null) m.new++;
          else m.existing++;

          b.total += meter.bills.length;
          b.duplicates += meter.bills.filter(bill => bill.duplicate).length;
        }
      }
    }

    return { legalEntities: le, serviceAddresses: sa, meters: m, bills: b };
  });

  constructor(private billService: BillService, private billImportService: BillImportService) {}

  ngOnInit(): void {
    if (this.context.customerId) {
      // Customer is known — skip customer step, go straight to entity review
      this.needsCustomerStep.set(false);
      this.currentStep.set('review');
      this.runMatch(Number(this.context.customerId));
    } else {
      // No customer — run match without customerId to get customer suggestions
      this.needsCustomerStep.set(true);
      this.currentStep.set('customer');
      this.runMatch(null);
    }
  }

  // ===========================================================================
  // Match Phase
  // ===========================================================================

  async runMatch(customerId: number | null = null): Promise<void> {
    const approvedBills = this.bills.filter(b => b.status === 'APPROVED' && b.data);
    if (approvedBills.length === 0) {
      this.error.set('No approved bills to import');
      return;
    }

    this.isLoading.set(true);
    this.isMatching.set(true);
    this.error.set(null);

    try {
      // Build match request from approved bills
      const request = this.buildMatchRequest(approvedBills, customerId);
      this.matchRequest.set(request);

      console.log('Match request:', JSON.stringify(request, null, 2));

      // Call match API
      const response = await this.billImportService.match(request).toPromise();
      this.matchResponse.set(response!);

      if (this.currentStep() === 'customer' && !customerId) {
        // First call — populate customer match and stay on customer step
        this.customerMatch.set(response!.customer ?? null);

        // Auto-select best match if high confidence
        const best = response!.customer?.match;
        if (best && best.score >= 0.8) {
          this.selectedCustomerId.set(best.id);
          this.selectedCustomerLabel.set(best.label);
        }
      } else {
        // Second call (or direct) — initialize entity selections
        this.initializeSelections(response!);
      }

    } catch (err: any) {
      console.error('Match error:', err);
      console.error('Match request was:', this.matchRequest());
      const errorMsg = err?.error?.message || err?.message || err?.statusText || 'Failed to match entities';
      const statusCode = err?.status ? ` (${err.status})` : '';
      this.error.set(`${errorMsg}${statusCode}`);
    } finally {
      this.isLoading.set(false);
      this.isMatching.set(false);
    }
  }

  // ===========================================================================
  // Customer Step
  // ===========================================================================

  selectCustomer(id: number | string | null, label: string): void {
    this.selectedCustomerId.set(id);
    this.selectedCustomerLabel.set(label);
  }

  async confirmCustomer(): Promise<void> {
    const custId = this.selectedCustomerId();

    if (custId === null) {
      // "Create New Customer" — skip second match, mark everything as new
      this.currentStep.set('review');
      this.selectedCustomerLabel.set(this.newCustomerPreview().suggestedName);
      // Use the response we already have but initialize all selections as "create new"
      const response = this.matchResponse();
      if (response) {
        this.initializeSelectionsAllNew(response);
      }
    } else {
      // Existing customer selected — re-run match with customerId for entity-level matches
      this.currentStep.set('review');
      await this.runMatch(Number(custId));
    }
  }

  goBackToCustomer(): void {
    this.currentStep.set('customer');
    this.error.set(null);
  }

  // ===========================================================================
  // Match Request Building
  // ===========================================================================

  private buildMatchRequest(approvedBills: BillFile[], customerId: number | null): BillImportMatchRequest {
    // Group bills by legal entity -> service address -> meter
    const entityMap = new Map<string, {
      name: string;
      addresses: Map<string, {
        street1: string;
        street2?: string;
        city: string;
        state: string;
        zip: string;
        meters: Map<string, {
          uan1: string;
          uan2?: string;
          utilityName?: string;
          commodityType: CommodityType;
          bills: BillFile[];
        }>;
      }>;
    }>();

    for (const bill of approvedBills) {
      const data = bill.data!;
      const entityName = (data.legalEntity || 'Unknown Entity').trim();
      const street1 = (data.serviceAddressStreet1 || '').trim();
      const street2 = (data.serviceAddressStreet2 || '').trim();
      const city = (data.serviceAddressCity || '').trim();
      const state = (data.serviceAddressState || '').trim();
      const zip = (data.serviceAddressZip || '').trim();
      const uan1 = (data.utilityAccountNumber || '').trim();
      const uan2 = (data.utilityAccountNumber2 || '').trim();
      const utilityName = (data.utilityName || '').trim();

      const addressKey = buildAddressGroupingKey(street1, city, state);
      // Meters are per-commodity: dual-fuel bills sharing a UAN group into
      // separate (elec/gas) meters.
      const meterKey = `${uan1 || `meter-${bill.id}`}|${billCommodity(bill)}`;

      // Get or create entity
      if (!entityMap.has(entityName)) {
        entityMap.set(entityName, { name: entityName, addresses: new Map() });
      }
      const entity = entityMap.get(entityName)!;

      // First bill to collide on the normalized key wins for display.
      // Don't stitch street1/street2 across bills — if the key says they're
      // the same SA, use one set of fields. Editing on the SA step lets
      // the user refine.
      if (!entity.addresses.has(addressKey)) {
        entity.addresses.set(addressKey, {
          street1,
          street2: street2 || undefined,
          city,
          state,
          zip,
          meters: new Map()
        });
      }
      const address = entity.addresses.get(addressKey)!;

      // Get or create meter
      if (!address.meters.has(meterKey)) {
        address.meters.set(meterKey, { uan1, uan2: uan2 || undefined, utilityName, commodityType: billCommodity(bill), bills: [] });
      }
      // Meter commodity is fixed by the grouping key; don't merge the bill's
      // file-level commodityTypes list (a dual-fuel PDF lists both commodities
      // on each of its per-commodity rows).
      const meterGroup = address.meters.get(meterKey)!;
      meterGroup.bills.push(bill);
    }

    // Build request structure
    const legalEntities: LegalEntityMatchInput[] = [];
    let groupCounter = 1;

    entityMap.forEach((entity) => {
      const lenGroupId = `len-${groupCounter++}`;
      const serviceAddresses: ServiceAddressMatchInput[] = [];

      entity.addresses.forEach((addr) => {
        const saGroupId = `sa-${groupCounter++}`;
        const meters: MeterMatchInput[] = [];

        addr.meters.forEach((meter) => {
          const meterGroupId = `meter-${groupCounter++}`;
          const bills: BillMatchInput[] = meter.bills.map(billFile => {
            const data = billFile.data!;
            return {
              groupId: `bill-${groupCounter++}`,
              billStartDate: data.billingPeriodStart || '',
              billEndDate: data.billingPeriodEnd || '',
              totalUsage: data.usageAmount,
              providerName: data.providerName
            };
          });

          meters.push({
            groupId: meterGroupId,
            utilityAccountNumber1: meter.uan1,
            utilityAccountNumber2: meter.uan2,
            utilityName: meter.utilityName,
            // Per-commodity meter groups — the meter key already includes commodity.
            commodityType: meter.commodityType,
            bills
          });
        });

        serviceAddresses.push({
          groupId: saGroupId,
          street1: addr.street1,
          street2: addr.street2,
          city: addr.city,
          state: addr.state,
          zip: addr.zip,
          meters
        });
      });

      legalEntities.push({
        groupId: lenGroupId,
        name: entity.name,
        serviceAddresses
      });
    });

    return {
      customerId,
      legalEntities
    } as BillImportMatchRequest;
  }

  private initializeSelections(response: BillImportMatchResponse): void {
    const newSelections = new Map<string, number | string | null>();

    for (const len of response.legalEntities) {
      // Use match if high confidence, otherwise create new
      if (len.match && len.match.score >= 0.8) {
        newSelections.set(len.groupId, len.match.id);
      } else {
        newSelections.set(len.groupId, null);
      }

      for (const sa of len.serviceAddresses) {
        if (sa.match && sa.match.score >= 0.8) {
          newSelections.set(sa.groupId, sa.match.id);
        } else {
          newSelections.set(sa.groupId, null);
        }

        for (const meter of sa.meters) {
          if (meter.match && meter.match.score >= 0.8) {
            newSelections.set(meter.groupId, meter.match.id);
          } else {
            newSelections.set(meter.groupId, null);
          }
        }
      }
    }

    this.selections.set(newSelections);
  }

  private initializeSelectionsAllNew(response: BillImportMatchResponse): void {
    const newSelections = new Map<string, number | string | null>();

    for (const len of response.legalEntities) {
      newSelections.set(len.groupId, null);
      for (const sa of len.serviceAddresses) {
        newSelections.set(sa.groupId, null);
        for (const meter of sa.meters) {
          newSelections.set(meter.groupId, null);
        }
      }
    }

    this.selections.set(newSelections);
  }

  // ===========================================================================
  // Selection Management
  // ===========================================================================

  getSelection(groupId: string): number | string | null {
    return this.selections().get(groupId) ?? null;
  }

  updateSelection(groupId: string, value: number | string | null): void {
    const current = this.selections();
    const updated = new Map(current);
    updated.set(groupId, value);
    this.selections.set(updated);
  }

  getOptions(match: MatchSuggestion | null, alternatives: MatchSuggestion[], extractedName: string): SelectOption[] {
    const options: SelectOption[] = [
      { value: null, label: `Create New: "${extractedName}"`, isCreateNew: true }
    ];

    if (match) {
      options.push({
        value: match.id,
        label: `${match.label} (${Math.round(match.score * 100)}% match)`,
        score: match.score
      });
    }

    for (const alt of alternatives || []) {
      if (!match || alt.id !== match.id) {
        options.push({
          value: alt.id,
          label: `${alt.label} (${Math.round(alt.score * 100)}% match)`,
          score: alt.score
        });
      }
    }

    return options;
  }

  // ===========================================================================
  // Import Phase
  // ===========================================================================

  async runImport(): Promise<void> {
    const response = this.matchResponse();
    const request = this.matchRequest();
    if (!response || !request) {
      this.error.set('No match data available');
      return;
    }

    this.isLoading.set(true);
    this.isImporting.set(true);
    this.error.set(null);

    try {
      const importRequest = this.buildImportRequest(response, request);
      const importResponse = await this.billImportService.import(importRequest).toPromise();

      if (importResponse!.success) {
        this.importComplete.emit(importResponse!);
      } else {
        const errorMessages = importResponse!.errors?.map(e => e.message).join(', ') || 'Unknown error';
        this.error.set(`Import failed: ${errorMessages}`);
      }

    } catch (err: any) {
      console.error('Import error:', err);
      const errorMsg = err?.error?.message || err?.message || err?.statusText || 'Failed to import bills';
      const statusCode = err?.status ? ` (${err.status})` : '';
      this.error.set(`${errorMsg}${statusCode}`);
    } finally {
      this.isLoading.set(false);
      this.isImporting.set(false);
    }
  }

  private buildImportRequest(matchResponse: BillImportMatchResponse, matchRequest: BillImportMatchRequest): BillImportRequest {
    const sel = this.selections();

    // Use flow-selected customer or context customer
    const customerId = this.selectedCustomerId() != null
      ? Number(this.selectedCustomerId())
      : (this.context.customerId ? Number(this.context.customerId) : null);

    const legalEntities: LegalEntityImport[] = matchResponse.legalEntities.map((lenResult, lenIdx) => {
      const lenInput = matchRequest.legalEntities[lenIdx];
      const lenSelectedId = sel.get(lenResult.groupId);

      return {
        groupId: lenResult.groupId,
        id: lenSelectedId != null ? String(lenSelectedId) : undefined,
        name: lenInput.name,
        serviceAddresses: lenResult.serviceAddresses.map((saResult, saIdx) => {
          const saInput = lenInput.serviceAddresses[saIdx];
          const saSelectedId = sel.get(saResult.groupId);

          return {
            groupId: saResult.groupId,
            id: saSelectedId != null ? Number(saSelectedId) : undefined,
            street1: saInput.street1,
            street2: saInput.street2,
            city: saInput.city,
            state: saInput.state,
            zip: saInput.zip,
            meters: saResult.meters.map((meterResult, meterIdx) => {
              const meterInput = saInput.meters[meterIdx];
              const meterSelectedId = sel.get(meterResult.groupId);

              return {
                groupId: meterResult.groupId,
                id: meterSelectedId != null ? Number(meterSelectedId) : undefined,
                utilityAccountNumber1: meterInput.utilityAccountNumber1,
                utilityAccountNumber2: meterInput.utilityAccountNumber2,
                utilityId: meterResult.utility?.utilityId ?? undefined,
                // A gas meter must carry its bills' native unit — omitted, the backend
                // defaults to Dth and a therm figure is stored 10x too large.
                unitId: this.meterUnitId(meterInput),
                bills: this.buildBillImports(meterResult, meterInput)
              } as MeterImport;
            })
          } as ServiceAddressImport;
        })
      } as LegalEntityImport;
    });

    return {
      customerId,
      legalEntities
    } as BillImportRequest;
  }

  /** The first of the meter's bills with a resolvable unit decides the meter unit; electricity stays the server default (kWh). */
  private meterUnitId(meterInput: MeterMatchInput): number | undefined {
    if (meterInput.commodityType !== CommodityType.Gas) {
      return undefined;
    }
    const bills = this.billService.bills();
    for (const billInput of meterInput.bills) {
      const data = bills.find(b => b.id === billInput.groupId)?.data;
      const unitId = data?.usageUnitId ?? gasUnitIdFromName(data?.usageUnit);
      if (unitId !== undefined) {
        return unitId;
      }
    }
    return undefined;
  }

  private buildBillImports(meterResult: MeterMatchResult, meterInput: MeterMatchInput): BillImport[] {
    const isBudgetingMode = this.billService.isBudgetingMode();
    return meterResult.bills.map((billResult, idx) => {
      const billInput = meterInput.bills[idx];
      // Find the actual bill file data
      const billFile = this.findBillFileByGroupId(billResult.groupId);
      const data = billFile?.data;

      return {
        groupId: billResult.groupId,
        billStartDate: billInput.billStartDate,
        billEndDate: billInput.billEndDate,
        totalAmount: data?.totalAmount || 0,
        totalUsage: data?.usageAmount || 0,
        demand: data?.demandKw,
        onPeakUsage: data?.onPeakUsage,
        intermediateUsage: data?.intermediateUsage,
        offPeakUsage: data?.offPeakUsage,
        onPeakCost: data?.onPeakCost,
        intermediateCost: data?.intermediateCost,
        offPeakCost: data?.offPeakCost,
        // Supply
        supplyTotal: data?.supplyTotalCost,
        supplyCharges: data?.supplyCharges,
        supplyTax: data?.supplyTaxes,
        contractCharges: data?.supplyContractCharges?.total,
        energyCharges: data?.supplyEnergyCharges?.total,
        capacityCharges: data?.supplyCapacityCharges?.total,
        transmissionCharges: data?.supplyTransmissionCharges?.total,
        marketCharges: data?.supplyMarketCharges?.total,
        lineLossCharges: data?.supplyLineLossCharges?.total,
        supplyGrtSut: data?.supplyGrtSut?.total,
        supplyMisc: data?.supplyMiscCharges?.total,
        // Supply — gas-only buckets (undefined on electricity bills)
        supplyCustomerCharges: data?.supplyCustomerCharges?.total,
        supplyGasCharges: data?.supplyGasCharges?.total,
        supplyOffPeakCharges: data?.supplyOffPeakCharges?.total,
        supplyRateClassCharges: data?.supplyRateClassCharges?.total,
        // Distribution
        distributionTotal: data?.distributionTotalCost,
        distributionCharges: data?.distributionCharges,
        distributionTax: data?.distributionTaxes,
        distributionCustomerCharges: data?.distributionCustomerCharges?.total,
        distributionEnergyCharges: data?.distributionEnergyCharges?.total,
        distributionDemandCharges: data?.distributionDemandCharges?.total,
        distributionMisc: data?.distributionMiscCharges?.total,
        // Distribution — gas-only buckets (undefined on electricity bills)
        distributionGasCharges: data?.distributionGasCharges?.total,
        distributionDeliveryCharges: data?.distributionDeliveryCharges?.total,
        distributionOffPeakCharges: data?.distributionOffPeakCharges?.total,
        distributionBalancingCharges: data?.distributionBalancingCharges?.total,
        distributionRateClassCharges: data?.distributionRateClassCharges?.total,
        distributionGrtSut: data?.distributionGrtSut?.total,
        supplyLineItems: lineItemsForImport(data, SUPPLY_BUCKET_KEYS, isBudgetingMode),
        distributionLineItems: lineItemsForImport(data, DISTRIBUTION_BUCKET_KEYS, isBudgetingMode),
        // Provider
        providerName: data?.providerName || undefined,
        supplierId: data?.supplierId || billResult.supplier?.supplierId || undefined,
        energyPlanId: data?.energyPlanId,
        // Type
        billType: data?.billType === 'supplier' ? 'DUAL' : 'CONSOLIDATED',
      } as BillImport;
    });
  }

  private findBillFileByGroupId(groupId: string): BillFile | undefined {
    // groupId format is "bill-N"
    const match = groupId.match(/^bill-(\d+)$/);
    if (!match) return undefined;
    // This is a simplified lookup - in practice we'd maintain a map
    const approvedBills = this.bills.filter(b => b.status === 'APPROVED' && b.data);
    // The groupId number doesn't directly map to index, so we need to find by position
    // For now, return undefined and let the code handle missing data gracefully
    return undefined;
  }

  // ===========================================================================
  // Template Helpers
  // ===========================================================================

  getOriginalName(groupId: string): string {
    const request = this.matchRequest();
    if (!request) return 'Unknown';

    for (const len of request.legalEntities) {
      if (len.groupId === groupId) return len.name || 'Unknown';
      for (const sa of len.serviceAddresses) {
        if (sa.groupId === groupId) {
          return [sa.street1, sa.city, sa.state].filter(Boolean).join(', ') || 'Unknown';
        }
        for (const meter of sa.meters) {
          if (meter.groupId === groupId) return meter.utilityAccountNumber1 || 'Unknown';
        }
      }
    }
    return 'Unknown';
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  cancel(): void {
    this.importCancelled.emit();
  }
}
